/** Launch & mapping → Tag categories (plan §2 S1): one compact table — a row per category,
 *  the key flags as columns, a details row with all 18 flags and the tag counts — with
 *  `use_operator_defaults` beside it.
 *
 *  - The tour's own rows are draft records (`tagcat:<id>`); the operator's defaults it
 *    inherits are listed read-only under them while it uses the operator defaults, each
 *    with Override (copies the row into the tour's own — one undo step) unless an own
 *    row already overrides it, and the operator's Defaults tab among its quick actions.
 *  - Add (a category picker), remove / restore, and up / down: each one undoable step on
 *    the membership list. A stored row's category is fixed (the PATCH ignores it).
 *  - A flag's dependants change with it in one step (`flagChanges`); a flag that cannot
 *    change on its own says why (`flagLock`).
 *  - Limits inline: at most 3 check-in and 1 boarding category among the categories the
 *    tour ends up with — turning one more on is not offered, and a draft that puts the
 *    tour over a limit (switching the operator defaults on) blocks Save with the reason;
 *    a tour already stored over a limit says so without blocking unrelated edits.
 *  - When the operator config does not allow per-tour categories, the table is read-only
 *    and says why (the PATCH would drop the rows without a word).
 *  - The block is `tour#tag-categories` (band node 5). */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';

import * as toursApi from '@/api/tours';
import type { PickerOption, TagCategoryM2M } from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { addRow, moveRow, removeRow, restoreRow, tableRows } from '@/features/records/collections/rows';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { BoolText, DraftCell, RowIssues, type CellChange } from '@/features/records/fields/DraftCell';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';

import { tagCategoryActions } from '../../tourLinks';
import { TAGCAT_ENTITY, TAGCAT_FLAG_LABELS, TOUR_ENTITY } from '../catalog';
import { COLLECTION_BY_ENTITY, TAGCAT_BOOLEANS } from '../collections';
import { tagCategoriesLockReason } from '../editorConfig';
import {
  effectiveCategories,
  flagChanges,
  flagLock,
  limitLock,
  limitProblems,
  type CategoryRow,
} from '../tagcatRules';
import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';
import { TourFieldGrid } from './TourSectionPanel';

const TAGCATS = COLLECTION_BY_ENTITY.get(TAGCAT_ENTITY)!;
const MEMBERSHIP = TAGCATS.membership;
/** The flags the table shows as columns (the rest are in the details row). */
const KEY_FLAGS = TAGCAT_FLAG_LABELS.filter((f) => f.short);
const LIMITED = new Set(['checkin', 'boarding']);

const HEAD_SX = { py: 0.5, px: 0.75, fontSize: 12, color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' } as const;
/** Flag headers wrap ("Self-select" over two lines) so seven columns fit ~720 px. */
const FLAG_HEAD_SX = { ...HEAD_SX, whiteSpace: 'normal', lineHeight: 1.2, px: 0.5 } as const;
const CELL_SX = { py: 0.25, px: 0.5, verticalAlign: 'middle' } as const;
const COLS = 2 + KEY_FLAGS.length;

export function TagCategoriesBlock() {
  const { detail, links, names } = useTourRecord();
  const { mode, registerFocus, errorsFor, setInvalidInput } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const editing = mode === 'edit';
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  const lock = tagCategoriesLockReason(detail);
  const canChange = editing && !lock && Boolean(detail.tenant) && !detail.is_deleted;
  const rows = tableRows(state, baselineOf, tourRec, TAGCATS, editing);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string, to?: boolean) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (to ?? !next.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });

  /** A row's value of a path: the draft's in edit mode, the stored one, a new row's default. */
  const valueOf = (rec: RecordKey, path: string): unknown => {
    const e = state.entries[rec]?.[path];
    if (e && editing) return e.to;
    const b = baselineOf(rec, path);
    return b === undefined ? TAGCATS.defaults[path] : b;
  };
  const tourValue = (path: string): unknown => {
    const e = state.entries[tourRec]?.[path];
    return e && editing ? e.to : baselineOf(tourRec, path);
  };
  const useDefaults = tourValue('use_operator_defaults') === true;

  const own: Array<CategoryRow & { id: number }> = rows
    .filter((r) => !r.removed)
    .map(({ id }) => {
      const rec = recordKey(TAGCAT_ENTITY, id);
      const c = valueOf(rec, 'tagcat.category');
      return {
        id,
        category: typeof c === 'number' ? c : null,
        checkin: valueOf(rec, 'tagcat.checkin') === true,
        boarding: valueOf(rec, 'tagcat.boarding') === true,
      };
    });
  const inheritedRows = detail.tag_categories_default;
  const inherited: CategoryRow[] = inheritedRows.map((r) => ({ category: r.category?.id ?? null, checkin: r.checkin, boarding: r.boarding }));
  const effective = effectiveCategories(own, inherited, useDefaults);
  // In both modes (Zero-Re-Layout): a stored breach reads the same in view and edit.
  const problems = limitProblems(effective);
  // Over a limit because of the draft: Save is blocked, with the reason (an invalid
  // input on the list). A tour stored over a limit (the server does not enforce it) only
  // says so — an unrelated edit still saves.
  const touched =
    editing &&
    (Object.keys(state.entries).some((rec) => rec.startsWith(`${TAGCAT_ENTITY}:`)) ||
      Boolean(state.entries[tourRec]?.[MEMBERSHIP] || state.entries[tourRec]?.use_operator_defaults));
  const problemText = touched ? problems.join(' · ') : '';

  useEffect(() => {
    setInvalidInput(tourRec, MEMBERSHIP, problemText || null);
  }, [problemText, tourRec, setInvalidInput]);
  useEffect(() => () => setInvalidInput(tourRec, MEMBERSHIP, null), [tourRec, setInvalidInput]);

  useEffect(
    () =>
      registerFocus(tourRec, MEMBERSHIP, () => {
        const el = document.getElementById('tour-block-tag-categories');
        el?.scrollIntoView?.({ block: 'start' });
        el?.focus({ preventScroll: true });
      }),
    [registerFocus, tourRec],
  );

  const nameOfRow = (id: number) => {
    const c = valueOf(recordKey(TAGCAT_ENTITY, id), 'tagcat.category');
    return typeof c === 'number' ? names.categoryLabel(c) : id < 0 ? `NEW-${-id}` : `#${id}`;
  };
  const usedCategories = new Set(own.map((r) => r.category));

  const addCategory = (o: PickerOption) => {
    names.rememberCategory({ id: o.id, name: o.name, code: o.code ?? null });
    const { action } = addRow(state, baselineOf, tourRec, TAGCATS, {
      seed: { 'tagcat.category': o.id },
      label: `Add the ${o.name ?? `#${o.id}`} category`,
    });
    dispatch(action);
  };
  const override = (r: TagCategoryM2M) => {
    if (!r.category) return;
    names.rememberCategory(r.category);
    const seed: Record<string, unknown> = { 'tagcat.category': r.category.id, 'tagcat.min_tags': r.min_tags, 'tagcat.max_tags': r.max_tags };
    for (const f of TAGCAT_BOOLEANS) seed[`tagcat.${f}`] = Boolean(r[f]);
    const { action } = addRow(state, baselineOf, tourRec, TAGCATS, { seed, label: `Override ${r.category.name ?? `#${r.category.id}`}` });
    dispatch(action);
  };
  const membershipErrors = editing ? errorsFor(tourRec, MEMBERSHIP).filter((e) => e !== problemText) : [];

  return (
    <BlockAnchor id="tag-categories" label="Tag categories">
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {/* The heading row holds "Add a category" at its end, its height reserved in both
            modes (Zero-Re-Layout). */}
        <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}>
          <Typography variant="caption" component="h3" color="text.secondary" noWrap sx={{ m: 0, fontWeight: 600, minWidth: 0 }}>
            Tag categories
            <Box component="span" sx={{ fontWeight: 400 }}>
              {' '}
              · {own.length} own · {useDefaults ? `${inheritedRows.length} from the operator` : 'operator defaults not used'}
            </Box>
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canChange && (
            <Box sx={{ width: 260, flex: 'none' }}>
              <RemoteAutocomplete<PickerOption>
                label="Add a category"
                errorText="Couldn’t load the operator’s tag categories"
                size="small"
                fetchOnEmpty
                value={null}
                getOptionLabel={(o) => o.name ?? `#${o.id}`}
                getOptionKey={(o) => o.id}
                getOptionMeta={(o) => ({ name: o.name ?? `#${o.id}`, pk: o.id, tenant: detail.tenant?.code ?? null, extra: [o.code] })}
                queryFn={async (q) => {
                  const r = await toursApi.picker(detail.tenant!.id, 'tag_categories', q);
                  // The "departure" category cannot be added to a fixed-date tour.
                  return r.results.filter((o) => !usedCategories.has(o.id) && !(detail.is_bespoke && o.code === 'departure'));
                }}
                onChange={(v) => {
                  const o = v as PickerOption | null;
                  if (o) addCategory(o);
                }}
              />
            </Box>
          )}
        </Box>
        <TourFieldGrid keys={['use_operator_defaults']} />
        {lock && (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-tagcats-locked>
            <LockOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
            <span>Read-only: {lock}.</span>
          </Typography>
        )}
        <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 600 }} aria-label="Tag categories" data-tagcats-table>
            <colgroup>
              <col style={{ width: '22%' }} />
              {KEY_FLAGS.map((f) => (
                <col key={f.flag} style={{ width: `${60 / KEY_FLAGS.length}%` }} />
              ))}
              <col style={{ width: '18%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_SX}>Category</TableCell>
                {KEY_FLAGS.map((f) => (
                  <TableCell key={f.flag} sx={FLAG_HEAD_SX}>
                    <Tooltip title={f.label}>
                      <span>{f.short}</span>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell sx={HEAD_SX}>
                  <Box component="span" sx={visuallyHidden}>
                    Row actions
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (!useDefaults || inheritedRows.length === 0) && (
                <TableRow>
                  <TableCell colSpan={COLS} sx={{ py: 1, color: 'text.secondary' }}>
                    No tag categories.
                  </TableCell>
                </TableRow>
              )}
              {rows.map(({ id, removed }, i) => (
                <OwnRow
                  key={id}
                  id={id}
                  index={i}
                  last={i === rows.filter((r) => !r.removed).length - 1}
                  name={nameOfRow(id)}
                  removed={removed}
                  open={open.has(`own:${id}`)}
                  onToggle={(to) => toggle(`own:${id}`, to)}
                  canChange={canChange}
                  effective={effective}
                  valueOf={valueOf}
                  links={links}
                  onMove={(delta) => {
                    const a = moveRow(state, baselineOf, tourRec, TAGCATS, id, delta, `Move ${nameOfRow(id)} ${delta < 0 ? 'up' : 'down'}`);
                    if (a) dispatch(a);
                  }}
                  onRemove={() => dispatch(removeRow(state, baselineOf, tourRec, TAGCATS, id, `Remove ${nameOfRow(id)}`))}
                  onRestore={() => dispatch(restoreRow(state, baselineOf, tourRec, TAGCATS, id, `Restore ${nameOfRow(id)}`))}
                />
              ))}
              {useDefaults &&
                inheritedRows.map((r) => (
                  <InheritedRow
                    key={`default:${r.id}`}
                    row={r}
                    overridden={usedCategories.has(r.category?.id ?? null)}
                    open={open.has(`default:${r.id}`)}
                    onToggle={() => toggle(`default:${r.id}`)}
                    canOverride={canChange}
                    onOverride={() => override(r)}
                  />
                ))}
            </TableBody>
          </Table>
        </Box>
        {problems.map((p) => (
          <Typography
            key={p}
            variant="caption"
            color={touched ? 'error' : 'warning.dark'}
            sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
            data-row-issue={touched ? 'invalid' : 'warning'}
          >
            {touched ? <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} /> : <WarningAmberIcon aria-hidden sx={{ fontSize: 16 }} />}
            {p}
            {touched ? '' : ' (stored like this — saving other changes is fine)'}
          </Typography>
        ))}
        {membershipErrors.map((p) => (
          <Typography key={p} variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-row-issue="invalid">
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            {p}
          </Typography>
        ))}
      </Box>
    </BlockAnchor>
  );
}

function OwnRow({
  id,
  index,
  last,
  name,
  removed,
  open,
  onToggle,
  canChange,
  effective,
  valueOf,
  links,
  onMove,
  onRemove,
  onRestore,
}: {
  id: number;
  index: number;
  last: boolean;
  name: string;
  removed: boolean;
  open: boolean;
  onToggle: (to?: boolean) => void;
  canChange: boolean;
  effective: CategoryRow[];
  valueOf: (rec: RecordKey, path: string) => unknown;
  links: { tenantId: number | null | undefined };
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onRestore: () => void;
}) {
  const { detail, names } = useTourRecord();
  const { mode, registerFocus } = useRecordEditor();
  const rec = recordKey(TAGCAT_ENTITY, id);
  const editing = mode === 'edit';
  const flagOf = (f: string) => valueOf(rec, `tagcat.${f}`) === true;
  const row = { flag: flagOf };
  const categoryId = valueOf(rec, 'tagcat.category');
  const category = typeof categoryId === 'number' ? names.categories.get(categoryId) : undefined;
  const changesFor = (flag: string) => (to: unknown): CellChange[] =>
    Object.entries(flagChanges(flag, to === true, row)).map(([f, v]) => ({ rec, path: `tagcat.${f}`, to: v }));
  const lockOf = (flag: string) => {
    const on = flagOf(flag);
    return flagLock(flag, row) ?? (LIMITED.has(flag) ? limitLock(flag, on, effective) : null);
  };

  // A ledger row for a details-row flag opens the details first.
  const detailPaths = TAGCAT_FLAG_LABELS.filter((f) => !f.short).map((f) => `tagcat.${f.flag}`).concat(['tagcat.min_tags', 'tagcat.max_tags']);
  useEffect(() => {
    const offs = detailPaths.map((path) =>
      registerFocus(rec, path, () => {
        onToggle(true);
        requestAnimationFrame(() =>
          document.querySelector<HTMLElement>(`[data-rec="${rec}"][data-cell="${path}"] input`)?.focus(),
        );
      }),
    );
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFocus, rec, open]);

  // Struck through on the row's amber tint — never an opacity over readable text (P3's rule).
  const struck = removed ? { textDecoration: 'line-through' } : {};
  const cells = [
    ...TAGCAT_FLAG_LABELS.map((f) => ({ rec, path: `tagcat.${f.flag}`, label: f.short ?? f.label })),
    { rec, path: 'tagcat.min_tags', label: 'Min tags' },
    { rec, path: 'tagcat.max_tags', label: 'Max tags' },
    { rec, path: 'tagcat.category', label: 'Category' },
  ];
  return (
    <>
      <TableRow data-tagcat-row={id} data-removed={removed || undefined} sx={[removed && { bgcolor: CHANGED_TINT }]}>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {category ? (
              <ValueActions actions={tagCategoryActions(category, detail.id, links)}>
                <Typography variant="body2" noWrap>
                  {name}
                </Typography>
              </ValueActions>
            ) : (
              <Typography variant="body2" noWrap>
                {name}
              </Typography>
            )}
            {id < 0 && editing && <Chip size="small" label="new" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
          </Box>
        </TableCell>
        {KEY_FLAGS.map((f) => (
          <TableCell key={f.flag} sx={{ ...CELL_SX, ...struck }}>
            {removed ? (
              <BoolText on={flagOf(f.flag)} onText="" offText="" />
            ) : (
              <DraftCell
                rec={rec}
                path={`tagcat.${f.flag}`}
                label={f.short!}
                rowName={name}
                changesFor={changesFor(f.flag)}
                disabledReason={lockOf(f.flag)}
                display={(v) => (
                  <BoolText on={v === true} onText="" offText="" />
                )}
              />
            )}
          </TableCell>
        ))}
        <TableCell sx={{ ...CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <Tooltip title={open ? 'Hide all flags' : 'All flags and tag counts'}>
            <IconButton size="small" aria-expanded={open} aria-label={`All flags of ${name}`} onClick={() => onToggle()}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {canChange && !removed && (
            <>
              <IconButton size="small" aria-label={`Move ${name} up`} disabled={index === 0} onClick={() => onMove(-1)}>
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" aria-label={`Move ${name} down`} disabled={last} onClick={() => onMove(1)}>
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Tooltip title={id < 0 ? 'Remove this new category' : 'Remove — deleted on save'}>
                <IconButton size="small" aria-label={`Remove ${name}`} onClick={onRemove}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canChange && removed && (
            <Tooltip title="Restore — keep this category">
              <IconButton size="small" aria-label={`Restore ${name}`} onClick={onRestore}>
                <RestoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {removed && editing && (
        <TableRow>
          <TableCell colSpan={COLS} sx={{ py: 0.25, px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
              {name} is removed from the tour when you save — Restore keeps it.
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {!removed && (
        <TableRow>
          <TableCell colSpan={COLS} sx={{ py: 0, px: 1, borderBottom: 0, '&:empty': { p: 0 } }}>
            <RowIssues cells={cells} />
          </TableCell>
        </TableRow>
      )}
      {open && !removed && (
        <TableRow data-tagcat-details={id}>
          <TableCell colSpan={COLS} sx={{ px: 1.5, py: 1, bgcolor: 'background.default' }}>
            <FlagDetails
              render={(flag, label) => (
                <DraftCell
                  rec={rec}
                  path={`tagcat.${flag}`}
                  label={label}
                  rowName={name}
                  showLabel
                  changesFor={changesFor(flag)}
                  disabledReason={lockOf(flag)}
                />
              )}
              counts={
                <>
                  <CountCell rec={rec} path="tagcat.min_tags" label="Min tags" name={name} />
                  <CountCell rec={rec} path="tagcat.max_tags" label="Max tags (0 = unlimited)" name={name} />
                </>
              }
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CountCell({ rec, path, label, name }: { rec: RecordKey; path: string; label: string; name: string }) {
  return (
    <Box sx={{ display: 'grid', gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <DraftCell rec={rec} path={path} label={label} rowName={name} />
    </Box>
  );
}

/** All flags, grouped as the dashboard groups them, and the tag counts. */
function FlagDetails({
  render,
  counts,
}: {
  render: (flag: string, label: string) => ReactNode;
  counts: ReactNode;
}) {
  const groups = [...new Set(TAGCAT_FLAG_LABELS.map((f) => f.group))];
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1 }}>{counts}</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1 }}>
        {groups.map((g) => (
          <Box key={g} sx={{ display: 'grid', gap: 0.25, alignContent: 'start' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {g}
            </Typography>
            {TAGCAT_FLAG_LABELS.filter((f) => f.group === g).map((f) => (
              <Box key={f.flag}>{render(f.flag, f.label)}</Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function InheritedRow({
  row,
  overridden,
  open,
  onToggle,
  canOverride,
  onOverride,
}: {
  row: TagCategoryM2M;
  overridden: boolean;
  open: boolean;
  onToggle: () => void;
  canOverride: boolean;
  onOverride: () => void;
}) {
  const { detail, links } = useTourRecord();
  const name = row.category?.name || row.category?.code || `#${row.id}`;
  const muted = overridden;
  return (
    <>
      <TableRow data-tagcat-default={row.id} data-overridden={overridden || undefined} sx={{ bgcolor: 'background.default' }}>
        <TableCell sx={CELL_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {row.category ? (
              <ValueActions actions={tagCategoryActions(row.category, detail.id, links, { inherited: true })}>
                <Typography variant="body2" noWrap color={muted ? 'text.secondary' : 'text.primary'}>
                  {name}
                </Typography>
              </ValueActions>
            ) : (
              <Typography variant="body2">{name}</Typography>
            )}
            <Typography variant="caption" color="text.secondary" noWrap>
              {overridden ? '· overridden' : '· from the operator'}
            </Typography>
          </Box>
        </TableCell>
        {KEY_FLAGS.map((f) => (
          <TableCell key={f.flag} sx={CELL_SX}>
            <BoolText on={Boolean(row[f.flag as keyof TagCategoryM2M])} onText="" offText="" muted />
          </TableCell>
        ))}
        <TableCell sx={{ ...CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <Tooltip title={open ? 'Hide all flags' : 'All flags and tag counts'}>
            <IconButton size="small" aria-expanded={open} aria-label={`All flags of ${name} (from the operator)`} onClick={onToggle}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {canOverride && !overridden && (
            <Tooltip title="Copy into the tour's own categories, to change it for this tour">
              <IconButton size="small" aria-label={`Override ${name}`} onClick={onOverride}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={COLS} sx={{ px: 1.5, py: 1, bgcolor: 'background.default' }}>
            <FlagDetails
              render={(flag, label) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
                  <BoolText on={Boolean(row[flag as keyof TagCategoryM2M])} onText="" offText="" muted />
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                </Box>
              )}
              counts={
                <Typography variant="body2" color="text.secondary">
                  Min tags {row.min_tags} · max tags {row.max_tags === 0 ? 'unlimited' : row.max_tags}
                </Typography>
              }
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
